// Purpose: admin audit search + CSV export.

import { api } from './client.ts';

export interface AuditRow {
  id: string;
  sequenceNumber: string;
  action: string;
  actorId: string | null;
  actorType: string;
  targetType: string | null;
  targetId: string | null;
  occurredAt: string;
  ipHash: string | null;
  payload: unknown;
  chainOk: boolean;
}

export interface AuditPage {
  rows: AuditRow[];
  nextCursor: string | null;
}

export interface AuditFilter {
  actions?: string[];
  actorId?: string;
  targetType?: string;
  targetId?: string;
  startDate?: string;
  endDate?: string;
}

function toParams(filter: AuditFilter): Record<string, string> {
  const out: Record<string, string> = {};
  if (filter.actions && filter.actions.length) out['actions'] = filter.actions.join(',');
  if (filter.actorId) out['actorId'] = filter.actorId;
  if (filter.targetType) out['targetType'] = filter.targetType;
  if (filter.targetId) out['targetId'] = filter.targetId;
  if (filter.startDate) out['startDate'] = filter.startDate;
  if (filter.endDate) out['endDate'] = filter.endDate;
  return out;
}

export async function searchAudit(
  filter: AuditFilter,
  cursor?: string,
  limit = 50
): Promise<AuditPage> {
  const params: Record<string, string> = { ...toParams(filter), limit: String(limit) };
  if (cursor) params['cursor'] = cursor;
  const res = await api.get<AuditPage>('/admin/audit', { params });
  return res.data;
}

// Commit 8b.12: drill-down detail returned by GET /api/admin/audit/:id.
export type AuditTargetDetail =
  | {
      kind: 'share';
      id: string;
      name: string;
      createdAt: string;
      status: string;
      recipientEmail: string | null;
      expiresAt: string | null;
    }
  | {
      kind: 'dropbox';
      id: string;
      label: string;
      createdAt: string;
      status: string;
      expiresAt: string | null;
    }
  | {
      kind: 'user';
      id: string;
      email: string;
      displayName: string;
      role: string;
      active: boolean;
    }
  | {
      kind: 'upload';
      id: string;
      filename: string;
      sizeBytes: string;
      mimeType: string;
      status: string;
    }
  | { kind: 'unknown'; targetType: string; targetId: string };

export interface AuditDetailNeighbor {
  id: string;
  sequenceNumber: string;
  action: string;
}

export interface AuditDetailResult {
  row: AuditRow;
  prev: AuditDetailNeighbor | null;
  next: AuditDetailNeighbor | null;
  target: AuditTargetDetail | null;
  related: AuditRow[];
}

export async function fetchAuditDetail(id: string): Promise<AuditDetailResult> {
  const res = await api.get<AuditDetailResult>(`/admin/audit/${encodeURIComponent(id)}`);
  return res.data;
}

// Triggers a browser download of the CSV + records chain proof from the
// response headers (X-AST-Chain-Proof + X-AST-Export-Hash) in a trailing
// chain-proof.json download.
export async function downloadAuditCsv(filter: AuditFilter): Promise<void> {
  const params: Record<string, string> = toParams(filter);
  const res = await api.get('/admin/audit/export.csv', {
    params,
    responseType: 'blob',
  });
  const blob = res.data as Blob;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'ast-audit-export.csv';
  a.click();
  URL.revokeObjectURL(a.href);
  const proof = res.headers['x-ast-chain-proof'];
  if (typeof proof === 'string') {
    const proofBlob = new Blob([proof], { type: 'application/json' });
    const p = document.createElement('a');
    p.href = URL.createObjectURL(proofBlob);
    p.download = 'ast-audit-chain-proof.json';
    p.click();
    URL.revokeObjectURL(p.href);
  }
}
